// MobileLayout.js
import React, { useState, useEffect } from "react";
import EmptyCanvasOverlay from "../components/InstructionsOverlay";
import TextTools from "../components/TextTools";
import Assets from "../components/Assets";
import DeepFry from "../components/DeepFry";
import Eraser from "../components/Eraser";
import Background from "../components/Background";
import HelpModal from "../components/HelpModal";
import Layers from "../components/Layers";

// Import your new icons
import dlicon from "../assets/icons/dlicon.png";
import deleteicon from "../assets/icons/delete.png";
import up from "../assets/icons/up.png";
import down from "../assets/icons/down.png";
import resetIcon from "../assets/icons/reset.png";
import questionIcon from "../assets/icons/question-mark.png";
import flip from "../assets/icons/flip.png";

// Import your helpers
import { downloadMemeHighRes } from "../helpers/downloadMeme";
import { bringLayerForward, sendLayerBackward } from "../helpers/moveLayers";
import { resetCanvas } from "../helpers/resetCanvas";

function MobileLayout({
	canvasRef,
	uploadedImage,
	uploadedImageObj,
	bgImage,
	setBgImage,
	activeTab,
	setActiveTab,
	layers,
	drawCanvas,
	updateAnySelected,
	selectedTextIsWhite,
	setSelectedTextIsWhite,
	selectedFontFamily,
	setSelectedFontFamily,
	isBold,
	setIsBold,
	isItalic,
	setIsItalic,
	isUnderlined,
	setIsUnderlined,
	isStrikethrough,
	setIsStrikethrough,
	isAllCaps,
	setIsAllCaps,
	textOpacity,
	setTextOpacity,
	clampTextInsideBgRect,
	topTextInput,
	setTopTextInput,
	bottomTextInput,
	setBottomTextInput,
	extraTextInputs,
	setExtraTextInputs,
	handleSetBackgroundFromUrl,
	handleAddNftAsAsset,
	handleRemoveBackground,
	handleImageUpload,
	category,
	setCategory,
	isAssetAdded,
	setIsAssetAdded,
	originalBgDataRef,
	preDeepFryCanvasRef,
	preDeepFryLayersRef,
	anySelected,
	handleDeleteSelected,
	isErasing,
	setIsErasing,
	eraserSize,
	setEraserSize,
	handleUndoLastErase,
	handleClearAllErases,
	eraserStrokesRef,
	bgRect,
	satValue,
	setSatValue,
	conValue,
	setConValue,
	briValue,
	setBriValue,
	hueValue,
	setHueValue,
	pixValue,
	setPixValue,
	noiseValue,
	setNoiseValue,
	hasDeepFried,
	setHasDeepFried,
	satEnabled,
	setSatEnabled,
	conEnabled,
	setConEnabled,
	briEnabled,
	setBriEnabled,
	hueEnabled,
	setHueEnabled,
	pixEnabled,
	setPixEnabled,
	noiseEnabled,
	setNoiseEnabled,
	setUploadedImage,
	forceUpdateLayers,
	layersVersion,
}) {
	const [menuOpen, setMenuOpen] = useState(false);

	const [showResetWarning, setShowResetWarning] = useState(false);
	const [canvasRect, setCanvasRect] = useState(null);

	const [showHelp, setShowHelp] = useState(false);

	// Measure the canvas so we can overlay the confirmation dialog
	useEffect(() => {
		if (canvasRef?.current) {
			const rect = canvasRef.current.getBoundingClientRect();
			setCanvasRect(rect);
		}
	}, [canvasRef]);

	// Whenever activeTab changes, disable erasing if we’re no longer on the “eraser” tab
	useEffect(() => {
		if (activeTab !== "eraser") {
			setIsErasing(false);
		}
	}, [activeTab, setIsErasing]);

	const handleDownloadMeme = async () => {
		// Reuse the exact same logic
		await downloadMemeHighRes({
			uploadedImageObj,
			layers,
			bgRect,
		});
	};

	const handleBringForward = () => {
		bringLayerForward(layers, drawCanvas, forceUpdateLayers);
	};

	const handleSendBackward = () => {
		sendLayerBackward(layers, drawCanvas, forceUpdateLayers);
	};

	const handleFlipSelected = () => {
		const selectedLayer = layers.current.find((l) => l.isSelected);
		if (!selectedLayer) return;

		if (selectedLayer.type === "asset") {
			selectedLayer.isFlipped = !selectedLayer.isFlipped;
			drawCanvas();
		}
	};

	// 4) Show/hide the Reset confirmation
	const handleShowResetConfirm = () => {
		setShowResetWarning(true);
	};
	const handleConfirmReset = () => {
		resetCanvas({
			setUploadedImage,
			setBgImage,
			uploadedImageObj,
			originalBgDataRef,
			layersRef: layers,
			eraserStrokesRef,
			setTopTextInput,
			setBottomTextInput,
			setExtraTextInputs,
			setIsAssetAdded,
			canvasRef,
		});
		setShowResetWarning(false);
	};
	const handleCancelReset = () => {
		setShowResetWarning(false);
	};

	return (
		<div className="flex flex-col items-center justify-center w-full overflow-x-hidden relative">
			{/* ========== CANVAS ON TOP ========== */}
			<div className="relative border-2 border-dashed border-monadGray p-2">
				<canvas ref={canvasRef} className="block max-w-full" />
				{!uploadedImage && <EmptyCanvasOverlay />}
			</div>

			{/* ========== ROW OF ICON BUTTONS BELOW CANVAS ========== */}
			<div className="w-full max-w-full flex flex-row justify-center items-center gap-5">
				{/* Logo */}
				<a
					href="https://hornsworld.xyz"
					target="_blank"
					rel="noopener noreferrer"
					className="flex items-center"
				>
					<img
						src="https://d39nah1etu3fy9.cloudfront.net/images/assets/sealuminati/hornelius_no_bg.png"
						alt="Hornsworld Logo"
						className="w-10 h-auto mt-1"
					/>
				</a>
				{/* Download Button */}
				<button
					onClick={handleDownloadMeme}
					title="Download Meme"
					disabled={!bgImage}
					className="flex items-center justify-center rounded shadow
               					transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={dlicon} alt="Download Meme" className="w-8 h-8" />
				</button>

				{/* Delete Button */}
				<button
					onClick={handleDeleteSelected}
					title="Delete Selected"
					disabled={!bgImage}
					className="flex items-center justify-center rounded shadow
								transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={deleteicon} alt="Delete Selected" className="w-6 h-6 mt-2" />
				</button>

				{/* Layer Up */}
				<button
					onClick={handleBringForward}
					title="Bring Selected Up"
					disabled={!bgImage}
					className="flex items-center justify-center rounded shadow
              					 transform transition duration-200 hover:scale-125
								 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={up} alt="Layer Up" className="w-8 h-8 mt-3" />
				</button>

				{/* Layer Down */}
				<button
					onClick={handleSendBackward}
					title="Send Selected Down"
					disabled={!bgImage}
					className="flex items-center justify-center rounded shadow
								transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={down} alt="Layer Down" className="w-8 h-8 mt-4" />
				</button>

				{/* Flip */}
				<button
					onClick={handleFlipSelected}
					title="Flip Selected Asset"
					disabled={!bgImage}
					className="flex items-center justify-center rounded shadow
               					transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={flip} alt="Flip Item Horizontally" className="w-9 h-9 mt-2" />
				</button>

				{/* Reset */}
				<button
					onClick={handleShowResetConfirm}
					title="Reset Canvas"
					disabled={!bgImage}
					className="flex items-center justify-center rounded shadow
               					transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={resetIcon} alt="Reset Canvas" className="w-7 h-7 mt-2" />
				</button>

				{/* Help */}
				<button
					onClick={() => setShowHelp(true)}
					title="Show Help"
					disabled={!bgImage}
					className="w-8 h-8 flex items-center justify-center rounded shadow
								transform transition duration-200 hover:scale-125
								disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<img src={questionIcon} alt="Help / Instructions" className="w-8 h-8 mt-2" />
				</button>
			</div>

			{/* 
        ========== BELOW: ROW WITH "MAIN MENU/COLLAPSE" + ACTIVE TAB CONTENT ==========
        The small pop-out menu is contained within a `relative` wrapper around the button.
      */}
			<div className="flex flex-row w-full p-4 max-w-full min-h-56 border-monadGray -mt-2">
				{/* WRAPPER for the vertical button + menu */}
				<div className="relative inline-block mr-4">
					{/* Vertical Button */}
					<button
						onClick={() => setMenuOpen((prev) => !prev)}
						className="bg-monadAccent text-white px-2 py-1 rounded-md inline-flex items-center justify-center"
					>
						<div
							className="leading-none"
							style={{ writingMode: "vertical-rl", textOrientation: "upright" }}
						>
							{menuOpen ? "← Hide  ←" : "→ Menu →"}
						</div>
					</button>

					{/* Pop-out Drawer for the menu buttons */}
					<div
						className={`
              absolute top-0 left-full ml-2 mt-0
              bg-monadDark text-white border border-monadGray
              rounded p-2
              flex flex-col
              space-y-2
              transition-all duration-300
              ${
								menuOpen
									? "opacity-100 visible pointer-events-auto"
									: "opacity-0 invisible pointer-events-none"
							}
              z-50
            `}
						style={{ minWidth: "8rem" }}
					>
						<button
							onClick={() => {
								setActiveTab("background");
								setMenuOpen(false);
							}}
							className={`px-2 py-1 rounded text-sm ${
								activeTab === "background"
									? "bg-monadAccent text-white"
									: "bg-monadDark text-white border border-monadGray"
							}`}
						>
							Background
						</button>
						<button
							onClick={() => {
								setActiveTab("text");
								setMenuOpen(false);
							}}
							className={`px-2 py-1 rounded text-sm ${
								activeTab === "text"
									? "bg-monadAccent text-white"
									: "bg-monadDark text-white border border-monadGray"
							}`}
						>
							Text
						</button>
						<button
							onClick={() => {
								setActiveTab("assets");
								setMenuOpen(false);
							}}
							className={`px-2 py-1 rounded text-sm ${
								activeTab === "assets"
									? "bg-monadAccent text-white"
									: "bg-monadDark text-white border border-monadGray"
							}`}
						>
							Assets
						</button>
						<button
							onClick={() => {
								setActiveTab("layers");
								setMenuOpen(false);
							}}
							className={`px-2 py-1 rounded text-sm ${
								activeTab === "layers"
									? "bg-monadAccent text-white"
									: "bg-monadDark text-white border border-monadGray"
							}`}
						>
							Layers
						</button>
						<button
							onClick={() => {
								setActiveTab("deepFry");
								setMenuOpen(false);
							}}
							className={`px-2 py-1 rounded text-sm ${
								activeTab === "deepFry"
									? "bg-monadAccent text-white"
									: "bg-monadDark text-white border border-monadGray"
							}`}
						>
							Deep Fry
						</button>
						<button
							onClick={() => {
								setActiveTab("eraser");
								setMenuOpen(false);
							}}
							className={`px-2 py-1 rounded text-sm ${
								activeTab === "eraser"
									? "bg-monadAccent text-white"
									: "bg-monadDark text-white border border-monadGray"
							}`}
						>
							Eraser
						</button>
					</div>
				</div>

				{/* ========== The Active Tab's Content ========== */}
				<div className="flex-1">
					{/* BACKGROUND TAB */}
					{activeTab === "background" && (
						<Background
							onNftBackground={handleSetBackgroundFromUrl}
							onNftAsset={handleAddNftAsAsset}
							onRemoveBackground={handleRemoveBackground}
							hasBackground={!!bgImage}
							onLocalBgFile={handleImageUpload}
						/>
					)}
					{/* TEXT TAB */}
					{activeTab === "text" && (
						<TextTools
							canvasRef={canvasRef}
							layers={layers}
							drawCanvas={drawCanvas}
							updateAnySelected={updateAnySelected}
							selectedTextIsWhite={selectedTextIsWhite}
							setSelectedTextIsWhite={setSelectedTextIsWhite}
							selectedFontFamily={selectedFontFamily}
							setSelectedFontFamily={setSelectedFontFamily}
							isBold={isBold}
							setIsBold={setIsBold}
							isItalic={isItalic}
							setIsItalic={setIsItalic}
							isUnderlined={isUnderlined}
							setIsUnderlined={setIsUnderlined}
							isStrikethrough={isStrikethrough}
							setIsStrikethrough={setIsStrikethrough}
							isAllCaps={isAllCaps}
							setIsAllCaps={setIsAllCaps}
							textOpacity={textOpacity}
							setTextOpacity={setTextOpacity}
							clampTextInsideBgRect={clampTextInsideBgRect}
							topTextInput={topTextInput}
							setTopTextInput={setTopTextInput}
							bottomTextInput={bottomTextInput}
							setBottomTextInput={setBottomTextInput}
							extraTextInputs={extraTextInputs}
							setExtraTextInputs={setExtraTextInputs}
							hasBackground={!!bgImage}
							bgRect={bgRect}
							forceUpdateLayers={forceUpdateLayers}
						/>
					)}

					{/* ASSETS TAB */}
					{activeTab === "assets" && (
						<Assets
							canvasRef={canvasRef}
							layers={layers}
							drawCanvas={drawCanvas}
							updateAnySelected={updateAnySelected}
							isMobile
							uploadedImage={uploadedImage}
							category={category}
							setCategory={setCategory}
							isAssetAdded={isAssetAdded}
							setIsAssetAdded={setIsAssetAdded}
							hasBackground={!!bgImage}
							bgRect={bgRect}
						/>
					)}

					{activeTab === "layers" && (
						<Layers
							layersRef={layers}
							drawCanvas={drawCanvas}
							forceUpdateLayers={forceUpdateLayers}
							layersVersion={layersVersion}
						/>
					)}

					{/* Eraser TAB */}
					{activeTab === "eraser" && (
						<Eraser
							canvasRef={canvasRef}
							layers={layers}
							isMobile
							uploadedImageObj={uploadedImageObj}
							uploadedImage={uploadedImage}
							setUploadedImage={setIsAssetAdded}
							setIsAssetAdded={setIsAssetAdded}
							originalBgDataRef={originalBgDataRef}
							preDeepFryCanvasRef={preDeepFryCanvasRef}
							preDeepFryLayersRef={preDeepFryLayersRef}
							drawCanvas={drawCanvas}
							anySelected={anySelected}
							isAssetAdded={isAssetAdded}
							onDeleteSelected={handleDeleteSelected}
							isErasing={isErasing}
							setIsErasing={setIsErasing}
							eraserSize={eraserSize}
							setEraserSize={setEraserSize}
							onUndoLastErase={handleUndoLastErase}
							onClearAllErases={handleClearAllErases}
							bgImage={bgImage}
							setBgImage={setBgImage}
							hasBackground={!!bgImage}
							eraserStrokesRef={eraserStrokesRef}
							bgRect={bgRect}
							setTopTextInput={setTopTextInput}
							setBottomTextInput={setBottomTextInput}
							setExtraTextInputs={setExtraTextInputs}
						/>
					)}

					{/* DEEP FRY TAB */}
					{activeTab === "deepFry" && (
						<DeepFry
							canvasRef={canvasRef}
							layers={layers}
							drawCanvas={drawCanvas}
							bgRect={bgRect}
							uploadedImageObj={uploadedImageObj}
							preDeepFryCanvasRef={preDeepFryCanvasRef}
							preDeepFryLayersRef={preDeepFryLayersRef}
							originalBgDataRef={originalBgDataRef}
							satValue={satValue}
							setSatValue={setSatValue}
							conValue={conValue}
							setConValue={setConValue}
							briValue={briValue}
							setBriValue={setBriValue}
							hueValue={hueValue}
							setHueValue={setHueValue}
							pixValue={pixValue}
							setPixValue={setPixValue}
							noiseValue={noiseValue}
							setNoiseValue={setNoiseValue}
							hasBackground={!!bgImage}
							hasDeepFried={hasDeepFried}
							setHasDeepFried={setHasDeepFried}
							satEnabled={satEnabled} 
							setSatEnabled={setSatEnabled}
							conEnabled={conEnabled} 
							setConEnabled={setConEnabled}
							briEnabled={briEnabled} 
							setBriEnabled={setBriEnabled}
							hueEnabled={hueEnabled} 
							setHueEnabled={setHueEnabled}
							pixEnabled={pixEnabled} 
							setPixEnabled={setPixEnabled}
							noiseEnabled={noiseEnabled} 
							setNoiseEnabled={setNoiseEnabled}
						/>
					)}
				</div>
			</div>
			{/* ===== Confirmation Dialog Overlay for Reset Canvas ===== */}
			{showResetWarning && canvasRect && (
				<div
					style={{
						position: "absolute",
						top: canvasRect.top + window.scrollY,
						left: canvasRect.left + window.scrollX,
						width: canvasRect.width,
						height: canvasRect.height,
						zIndex: 999,
					}}
				>
					{/* Center the dialog box inside the overlay */}
					<div
						className="
              absolute
              top-1/2
              left-1/2
              -translate-x-1/2
              -translate-y-1/2
              bg-monadAccent
              rounded-md
              w-[80%]
              max-w-[300px]
              p-6
              text-center
              border-2
              border-monadGray
              shadow-xl
            "
					>
						<h2 className="text-sm md:text-base font-bold mb-4 text-monadWhite">
							Are you sure you want to reset?
						</h2>
						<p className="text-monadWhite text-sm md:text-base mb-4">
							This will erase the current state of the canvas.
						</p>
						<div className="flex gap-4 justify-center">
							<button
								onClick={handleConfirmReset}
								className="
                  text-sm md:text-base px-4 py-2 rounded-md bg-red-600 text-white
                  hover:bg-red-700 transition-colors
                "
							>
								Reset Canvas
							</button>
							<button
								onClick={handleCancelReset}
								className="
                  text-sm md:text-base px-4 py-2 rounded-md bg-gray-300 text-black
                  hover:bg-gray-400 transition-colors
                "
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
			{showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
		</div>
	);
}

export default MobileLayout;
